import React from 'react';

/**
 * ResultSummary — global banner at the top of the results panel.
 * Shows the overall compatibility status of the N selected skills.
 */

const ResultSummary = ({ summary, skillCount }) => {
    if (!summary || summary.totalAnalyzed === 0) {
        return (
            <div className="flex items-center gap-3 p-4 rounded-xl
                bg-slate-800/60 border border-slate-700">
                <svg className="w-5 h-5 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-slate-400">
                    No shared conditions found between selected skills.
                </p>
            </div>
        );
    }

    if (summary.hasRaceConflict) {
        return (
            <div className="relative overflow-hidden flex items-start gap-3 p-4 rounded-xl
                bg-red-900/20 border border-red-700/50">
                <div className="absolute inset-0 bg-gradient-to-r from-red-900/10 to-transparent pointer-events-none" />
                <svg className="relative w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="relative">
                    <p className="text-sm font-bold text-red-300">
                        {summary.raceConflictCount} race-level conflict{summary.raceConflictCount !== 1 ? 's' : ''} detected
                    </p>
                    <p className="text-xs text-red-400/80 mt-0.5">
                        These skills require different race conditions — they may never trigger together in any race.
                    </p>
                </div>
            </div>
        );
    }

    if (summary.hasMomentConflict) {
        return (
            <div className="flex items-start gap-3 p-4 rounded-xl
                bg-amber-900/20 border border-amber-700/50">
                <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                    <p className="text-sm font-bold text-amber-300">
                        {summary.momentConflictCount} moment conflict{summary.momentConflictCount !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-amber-400/80 mt-0.5">
                        Skills can coexist in the same race, but won't trigger at the exact same moment.
                    </p>
                </div>
            </div>
        );
    }

    // Partial overlap — orange
    if (summary.hasPartialOverlap && !summary.hasRaceConflict && !summary.hasMomentConflict) {
        return (
            <div className="flex items-start gap-3 p-4 rounded-xl
                bg-orange-900/20 border border-orange-700/50">
                <svg className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <div>
                    <p className="text-sm font-bold text-orange-300">
                        {summary.partialCount} partial overlap{summary.partialCount !== 1 ? 's' : ''} detected
                    </p>
                    <p className="text-xs text-orange-400/80 mt-0.5">
                        Skills share some conditions via OR paths, but not all. They can coexist in specific scenarios.
                    </p>
                </div>
            </div>
        );
    }

    // All compatible — golden glow
    return (
        <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500/30 via-teal-500/20 to-emerald-500/30
                rounded-xl blur opacity-50 group-hover:opacity-80 transition-opacity duration-700" />
            <div className="relative flex items-start gap-3 p-4 rounded-xl
                bg-emerald-900/20 border border-emerald-700/50">
                <svg className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                        d="M5 13l4 4L19 7" />
                </svg>
                <div>
                    <p className="text-sm font-bold text-emerald-300">
                        All {skillCount} skills are compatible
                    </p>
                    <p className="text-xs text-emerald-400/80 mt-0.5">
                        No conflicts detected. These skills can be used together effectively.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ResultSummary;
